import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { FileText, ArrowLeft } from 'lucide-react';
import {
  InvoicesWrapper,
  InvoicesContainer,
  HeaderSection,
  SectionTitle,
  BackButton,
  InvoiceList,
  InvoiceCard,
  InvoiceCardBody,
  InvoiceIcon,
  InvoiceInfo,
  InvoiceTitle,
  InvoiceMeta,
  ViewButton,
  PaginationSection,
  PaginationButton,
  PaginationInfo,
  EmptyState,
} from './Invoices.styles';
import { getInvoiceList } from '../../../services/general';

const PAGE_SIZE = 10;

const Invoices = () => {
  const [page, setPage] = useState(0);

  const navigate = useNavigate();
  const { chatbotId } = useParams();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['invoiceList', chatbotId, page],
    queryFn: () =>
      getInvoiceList({
        page,
        pageSize: PAGE_SIZE,
        chatbotId,
      }),
    enabled: !!chatbotId,
    placeholderData: (previousData) => previousData,
  });

  const invoices = data?.invoices ?? [];
  const totalRecords = data?.pagination?.total ?? invoices.length;
  const limit = data?.pagination?.limit ?? PAGE_SIZE;
  const maxPages = Math.ceil(totalRecords / limit) || 1;
  const start = totalRecords === 0 ? 0 : page * limit + 1;
  const end = Math.min((page + 1) * limit, totalRecords);
  const hasPrev = page > 0;
  const hasNext = page + 1 < maxPages;

  const handlePreviousPage = () => setPage((value) => value - 1);
  const handleNextPage = () => setPage((value) => value + 1);

  const handleBack = () => navigate(-1);

  const handleViewInvoice = (invoiceId) => {
    navigate(`/invoice/${invoiceId}`);
  };

  const formatDate = (isoDate) =>
    new Date(isoDate).toLocaleDateString('en-AU', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });

  const renderContent = () => {
    if (isLoading) {
      return (
        <EmptyState>
          <p>Loading invoices…</p>
        </EmptyState>
      );
    }

    if (isError) {
      return (
        <EmptyState>
          <p>Failed to load invoices</p>
          <p>Please try again later</p>
        </EmptyState>
      );
    }

    if (invoices.length === 0) {
      return (
        <EmptyState>
          <p>No invoices found</p>
          <p>Invoices generated for this chatbot will appear here</p>
        </EmptyState>
      );
    }

    return (
      <>
        <InvoiceList>
          {invoices.map((invoice) => (
            <InvoiceCard key={invoice._id || invoice.id}>
              <InvoiceCardBody>
                <InvoiceIcon>
                  <FileText />
                </InvoiceIcon>

                <InvoiceInfo>
                  <InvoiceTitle>{invoice.title}</InvoiceTitle>
                  <InvoiceMeta>Created on {formatDate(invoice.createdAt)}</InvoiceMeta>
                </InvoiceInfo>
              </InvoiceCardBody>

              <ViewButton onClick={() => handleViewInvoice(invoice._id || invoice.id)}>
                View
              </ViewButton>
            </InvoiceCard>
          ))}
        </InvoiceList>

        <PaginationSection>
          <PaginationButton onClick={handlePreviousPage} disabled={!hasPrev}>
            Previous
          </PaginationButton>

          <PaginationInfo>
            {start} - {end} of {totalRecords}
          </PaginationInfo>

          <PaginationButton onClick={handleNextPage} disabled={!hasNext}>
            Next
          </PaginationButton>
        </PaginationSection>
      </>
    );
  };

  return (
    <InvoicesWrapper>
      <InvoicesContainer>
        <HeaderSection>
          <div>
            <SectionTitle>Invoices</SectionTitle>
          </div>
          <BackButton onClick={handleBack}>
            <ArrowLeft />
            Back
          </BackButton>
        </HeaderSection>

        {renderContent()}
      </InvoicesContainer>
    </InvoicesWrapper>
  );
};

export default Invoices;
